import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ReportingDataService } from '../shared/services/reporting-data.service';
import { ReportingOperationsComponent } from './reporting-operations.component';
import { ReportingValueComponent } from './reporting-value.component';

@Component({
  selector: 'app-reporting-snapshot',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ReportingOperationsComponent,
    ReportingValueComponent
  ],
  templateUrl: './reporting-snapshot.component.html',
  styleUrl: './reporting-snapshot.component.scss'
})
export class ReportingSnapshotComponent {

  activeProgramPage: 'executiveOverview' | 'edcOperations' | 'valueAndImpact' = 'executiveOverview';

  selectProgramPage(page: 'executiveOverview' | 'edcOperations' | 'valueAndImpact'): void {
    this.activeProgramPage = page;
  }

  readonly kpiMetrics = ['Deployment', 'Health', 'Utilization', 'Adoption'];
  get currentEventsRows() { return this.dataService.currentEventsRows; }
  get scorecardRows() { return this.dataService.scorecardRows; }
  get recentAlerts() { return this.dataService.recentAlerts; }
  readonly pageFilters = ['24 Hours', 'All NAMCs', 'All Priorities'];
  readonly xAxisLabels = ['W1', 'W4', 'W8', 'W12'];

  activeMetric = this.kpiMetrics[0];
  readonly chartByMetric: Record<string, { ticks: number[]; format: (value: number) => string; series: Array<{ name: string; color: string; values: number[] }> }> = {
    Deployment: {
      ticks: [100, 90, 80, 70, 60, 50],
      format: (v: number) => `${v}%`,
      series: [
        { name: 'TABC', color: '#2477b5', values: [74, 77, 81, 84] },
        { name: 'TMMC', color: '#f28c28', values: [69, 72, 75, 79] },
        { name: 'TMMI', color: '#2d9d78', values: [61, 64, 68, 72] }
      ]
    },
    Health: {
      ticks: [100, 90, 80, 70, 60, 50],
      format: (v: number) => `${v}%`,
      series: [
        { name: 'TABC', color: '#2477b5', values: [88, 89, 90, 91] },
        { name: 'TMMC', color: '#f28c28', values: [82, 84, 85, 86] },
        { name: 'TMMI', color: '#2d9d78', values: [76, 78, 79, 81] }
      ]
    },
    Utilization: {
      ticks: [100, 90, 80, 70, 60, 50],
      format: (v: number) => `${v}%`,
      series: [
        { name: 'TABC', color: '#2477b5', values: [66, 69, 72, 76] },
        { name: 'TMMC', color: '#f28c28', values: [70, 73, 76, 79] },
        { name: 'TMMI', color: '#2d9d78', values: [58, 61, 64, 67] }
      ]
    },
    Adoption: {
      ticks: [100, 90, 80, 70, 60, 50],
      format: (v: number) => `${v}%`,
      series: [
        { name: 'TABC', color: '#2477b5', values: [63, 67, 71, 74] },
        { name: 'TMMC', color: '#f28c28', values: [60, 64, 67, 71] },
        { name: 'TMMI', color: '#2d9d78', values: [55, 59, 62, 65] }
      ]
    }
  };

  selectedTab: 'events' | 'scorecard' | 'alerts' = 'events';

  get activeChart() {
    return this.chartByMetric[this.activeMetric];
  }

  selectMetric(metric: string): void {
    this.activeMetric = metric;
  }

  getTickY(tick: number): number {
    const ticks = this.activeChart.ticks;
    const maxTick = Math.max(...ticks);
    const minTick = Math.min(...ticks);
    const normalized = (tick - minTick) / Math.max(maxTick - minTick, 1);
    return 100 - normalized * 100;
  }

  getLinePoints(values: number[]): string {
    if (!values.length) {
      return '';
    }
    const ticks = this.activeChart.ticks;
    const maxTick = Math.max(...ticks);
    const minTick = Math.min(...ticks);
    const xStep = values.length > 1 ? 100 / (values.length - 1) : 0;
    return values
      .map((value, index) => {
        const x = index * xStep;
        const yNorm = (value - minTick) / Math.max(maxTick - minTick, 1);
        const y = 100 - yNorm * 100;
        return `${x},${y}`;
      })
      .join(' ');
  }

  scoreLevel(score: number): 'good' | 'warn' | 'critical' {
    if (score >= 80) {
      return 'good';
    }
    if (score >= 65) {
      return 'warn';
    }
    return 'critical';
  }

  formatOrDash(value: number | null): string {
    return value === null ? '-' : `${value}`;
  }

  trackByName(_: number, row: { name?: string; [key: string]: any }): string {
    return row.name || '';
  }

  trackByIndex(index: number): number { return index; }

  constructor(private readonly dataService: ReportingDataService) {}
}
